import { FormProvider, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { PageContainer, ScrollableTable } from "../../components";
import AnalyticsFilter from "./sections/header/AnalyticsFilter";
import AnalyticsHeader from "./sections/header/AnalyticsHeader";
import EventMetricsCard from "../../components/EventMetricsCard/EventMetricsCard";
import * as Styled from './AnalyticsPage.styles';
import { Typography } from "@mui/material";
import { useState } from "react";
import { ColumnDef } from "../../components/ScrollableTable/ScrollableTable";
import axiosAPI from "../../services/axios";
import { GET_ANALYTICS } from "../../services/endpoints";
import { Event } from "../../types/event";

// enum DashboardToggle {
//   DASHBOARD,
//   COMPARISON_VIEW,
// }

const OVERALL_METRICS_LABELS: { [x: string]: string } = {
  "totalEvents": "Events",
  "attendanceRate": "Attendence Rate",
  "totalAttendees": "Total Attendees",
  "signUps": "Sign ups",
  "plannedAttendees": "Planned Attendees",
  "walkIns": "Walk ins",
  "noShows": "No Shows",
}

interface OverallEventMetric {
  label: string;
  value: number | string;
}

const AnalyticsPage = () => {

  // const [dashboardToggle, setDashboardToggle] = useState(DashboardToggle.DASHBOARD);
  const [allTableData, setAllTableData] = useState<Event[]>([]);
  const [overallMetrics, setOverallMetrics] = useState<OverallEventMetric[]>()

  const currentDate = dayjs();
  const thirtyDaysPast = currentDate.subtract(1, "month");

  const formMethods = useForm({
    defaultValues: {
      dateStart: thirtyDaysPast,
      dateEnd: currentDate,
      eventTags: ["All"],
      eventFormat: ["All"],
      eventType: ["All"],
      location: "All",
      eventMetrics: [],
    },
    mode: "onChange"
  });

  const selectedEventTags = formMethods.watch("eventTags");
  const selectedEventFormats = formMethods.watch("eventFormat");
  const selectedEventTypes = formMethods.watch("eventType");
  const selectedLocations = formMethods.watch("location");

  const handleSearch = async () => {
    const startDate = Math.floor((formMethods.getValues("dateStart").valueOf()) / 1000).toString();
    const endDate = Math.floor((formMethods.getValues("dateEnd").valueOf()) / 1000).toString();
    const filterAll = (options: string[]) => options.filter((option) => option !== "All");
    const { data } = await axiosAPI.get(
      GET_ANALYTICS(
        startDate,
        endDate,
        filterAll(selectedEventFormats),
        filterAll(selectedEventTags),
        filterAll(selectedEventTypes),
        filterAll([selectedLocations])
      )
    );

    const formattedData = Object.entries(data.result.overallMetrics).map(([key, val]) => {
      return {
        label: key,
        value:
          key === "attendanceRate" && typeof val === "number"
            ? `${val.toFixed(1)}%`
            : val,
      } as OverallEventMetric;
    });

    setOverallMetrics(formattedData);
    setAllTableData(
      (data.result.result ?? []).map((event: Event) => ({
        ...event,
        plannedAttendees: event.plannedAttendees ?? 0,
      }))
    );
  };

  const handleClearAllFilters = () => {
    formMethods.reset({
      dateStart: thirtyDaysPast,
      dateEnd: currentDate,
      eventTags: ["All"],
      eventFormat: ["All"],
      eventType: ["All"],
      location: "All",
      eventMetrics: [],
    });
  };


  const tableColumns: ColumnDef[] = [
    { id: "title", label: "Event Name", sortable: true },
    { id: "totalAttendeeCount", label: "Total Attendees", sortable: true, align: "center", tooltip: "Total event check ins, which is the sum of all planned attendees and walk ins" },
    { id: "plannedAttendees", label: "Planned Attendees", sortable: true, align: "center", tooltip: "The number of attendees planned for the event" },
    { id: "signUpCount", label: "Sign Ups", sortable: true, align: "center", tooltip: "The number of people who signed up to attend the event using the Gather app" },
    { id: "attendanceRate", label: "Attendance Rate", sortable: true, align: "center", tooltip: "Planned attendees / Sign ups" },
    { id: "walkInCount", label: "Walk Ins", sortable: true, align: "center", tooltip: "The number of attendees who did not sign up beforehand but checked into the event using the Gather app" },
    { id: "noShowCount", label: "No Shows", sortable: true, align: "center", tooltip: "The number of people who signed up for the event but did not attend" },
    { id: "format", label: "Event Format", sortable: true, align: "center" },
    { id: "office", label: "Location", sortable: true, align: "center" },
  ];


  return (
    <>
      <PageContainer sx={{ justifyContent: "initial" }}>
        <FormProvider {...formMethods}>
          <form>
            <div>
              <AnalyticsHeader/>
              <AnalyticsFilter formMethods={formMethods} onClearAll={handleClearAllFilters} onSearch={handleSearch}></AnalyticsFilter>
            </div>
          </form>
        </FormProvider>
        <div style={{backgroundColor: "#F7F7F7", justifyContent: "initial", paddingInline: "36px", height: "100%"}}>
          <Typography variant="h2" style={{padding: "25px 0px"}}>Overall Event Metrics</Typography>
          <Styled.EventMetricsContainer>
            {overallMetrics && overallMetrics.map((metric) => <EventMetricsCard value={metric.value.toString()} label={OVERALL_METRICS_LABELS[metric.label]}></EventMetricsCard>)}
          </Styled.EventMetricsContainer>
          <Typography variant="h2" style={{padding: "25px 0px"}}>Event Details</Typography>
          <ScrollableTable<Event>
            columns={tableColumns}
            rows={allTableData}
            maxHeight="472px"
            rowKey="eventId"
          />
        </div>
      </PageContainer>
    </>
  );
};

export default AnalyticsPage;